import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { inventoryService } from "../utils/services/inventoryService";
import { toast } from "react-toastify";
import { forecastDataService } from "../utils/services/forecast-data.service";
import { staticDataService } from "../utils/services/static-data.service";

const ForecastContext = createContext();

export const ForecastProvider = ({ children }) => {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sku, setSku] = useState(null);
  const [expectedTime, setExpectedTime] = useState(null);
  const [forecastedSKU, setForecastedSKU] = useState([]);
  const [showExplanation, setShowExplanation] = useState(false);

  // ── Forecast params modal state ──
  const [forecastDialogOpen, setForecastDialogOpen] = useState(false);
  const [forecastItems, setForecastItems] = useState([]);
  const [horizon, setHorizon] = useState(3);
  const [horizonType, setHorizonType] = useState("Monthly");
  const [selectedWarehouse, setSelectedWarehouse] = useState(null);
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [bulkResponses, setBulkResponses] = useState([]);
  const [bulkModalOpen, setBulkModalOpen] = useState(false);
  const [forecastBy, setForecastBy] = useState("SKU");
  const [forecastByLocked, setForecastByLocked] = useState(false);
  const [discountMin, setDiscountMin] = useState(0);
  const [discountMax, setDiscountMax] = useState(0);
  const [discountType, setDiscountType] = useState("%");
  const [discountMode, setDiscountMode] = useState("flat");
  const [distributedDiscounts, setDistributedDiscounts] = useState({});
  const [discountSectionCollapsed, setDiscountSectionCollapsed] =
    useState(true);
  const [percentageRange, setPercentageRange] = useState("");
  const [similarProductDialogOpen, setSimilarProductDialogOpen] =
    useState(false);
  const [similarProducts, setSimilarProducts] = useState({});
  const [similarProductSelections, setSimilarProductSelections] = useState({});
  const [similarActiveTab, setSimilarActiveTab] = useState(0);
  const [similarProductLoading, setSimilarProductLoading] = useState(false);
  const [forecastProductName, setForecastProductName] = useState(null);
  const [forecastType, setForecastType] = useState("warehouse");
  const [includeLostSales, setIncludeLostSales] = useState(false);
  const [searchWindowDays, setSearchWindowDays] = useState(180);
  const [demandWindowDays, setDemandWindowDays] = useState(null);
  const [dowAdjustmentEnabled, setDowAdjustmentEnabled] = useState(true);
  const [editingLostSales, setEditingLostSales] = useState(false);

  // Supply-planning overrides — sent with every forecast item.
  const [leadTimeDays, setLeadTimeDays] = useState(21);
  const [safetyStockPct, setSafetyStockPct] = useState(10); // % of on hand

  // Callbacks provided by the consumer when opening the dialog
  const _forecastCallbacks = useRef({});

  // Reset dialog state whenever forecast params dialog opens
  useEffect(() => {
    if (forecastDialogOpen) {
      setHorizon(3);
      setHorizonType("Monthly");
      setDiscountMin(0);
      setDiscountMax(0);
      setDiscountType("%");
      setDiscountMode("flat");
      setDistributedDiscounts({});
      setDiscountSectionCollapsed(true);
      setPercentageRange("");
      setSelectedWarehouse(null);
      setSelectedChannel(null);
      setForecastBy(_forecastCallbacks.current.initialForecastBy || "SKU");
      setForecastByLocked(!!_forecastCallbacks.current.initialForecastBy);
      setIncludeLostSales(false);
      setSearchWindowDays(180);
      setDemandWindowDays(null);
      setDowAdjustmentEnabled(true);
      setEditingLostSales(false);
      setLeadTimeDays(21);
      setSafetyStockPct(10);
    }
  }, [forecastDialogOpen]);

  /**
   * Open the forecast parameters modal.
   * @param {Array} items  - selected inventory items to forecast
   * @param {Object} callbacks - { onRefresh, onClearSelected, initialForecastBy } from the caller
   * @param {string} type - the type of forecast to be generated
   */
  const openForecastParamsModal = (
    items,
    callbacks = {},
    type = "warehouse",
  ) => {
    setForecastedSKU([]);
    setForecastItems(items);
    _forecastCallbacks.current = callbacks;
    setForecastProductName(
      callbacks.initialForecastBy === "Product" ? items[0]?.name || null : null,
    );
    setForecastDialogOpen(true);
    setForecastType(type);
  };

  const handlePercentageRangeChange = (range) => {
    setPercentageRange(range);
    if (range === "custom") {
      setDiscountMax("");
    } else {
      setDiscountMax(Number(range));
    }
  };

  const clearForecastingData = () => {
    setForecastLoading({ open: false, sku: null, expectedTime: null });
  };

  const handleBulkForecast = async (similarProductMap = {}) => {
    const selectedInventories = forecastItems;
    if (selectedInventories.length === 0) return;
    let forecastDataSet = false;

    // Clear all stale forecast data before starting a new run
    setData(null);
    setBulkResponses([]);
    setForecastedSKU([]);
    setBulkModalOpen(false);

    const skuList =
      forecastBy?.toLowerCase() === "product"
        ? [
            ...new Set(
              selectedInventories.map(
                (x) => x.product?.name || x.name || x.sku,
              ),
            ),
          ]
        : selectedInventories.map((x) => x.sku);
    // Show loading immediately so there is no gap after the params modal closes
    setForecastLoading({ open: true, sku: skuList, expectedTime: null });
    try {
      const staticDataResponse =
        await staticDataService.forecastExecutionTime();
      if (staticDataResponse?.data?.[0]?.value) {
        setExpectedTime(staticDataResponse.data[0].value);
      }
      let responseData = [];
      try {
        responseData = await inventoryService.bulkForecast({
          items: selectedInventories.map((x) => ({
            sku: x.sku,
            product: x.product._id,
            category: x.product.category,
            product_type: x.product.product_type,
            horizon: horizon,
            type: horizonType,
            channel:
              forecastType === "channel" ? x.provider : selectedChannel?.name,
            warehouse:
              forecastType === "warehouse"
                ? x.warehouse?._id
                : selectedWarehouse?._id,
            discount:
              discountMode === "flat"
                ? discountMax === "" || discountMax == null
                  ? 0
                  : Number(discountMax) || 0
                : distributedDiscounts[x._id]?.max,
            forecast_by: forecastBy,
            lead_time_days: Number(leadTimeDays) || 0,
            safety_stock_percentage: Number(safetyStockPct) || 0,
            ...(Object.keys(similarProductMap).length > 0 && {
              product: similarProductMap[x._id],
              actual_product: x.product._id,
              actual_sku: x.sku,
            }),
            client: x.client,
          })),
          bulk: true,
          forecast_type: forecastType,
          engine: "kaman",
          ...(includeLostSales && {
            include_lost_sales: true,
            search_window_days: searchWindowDays,
            demand_window_days: demandWindowDays,
            dow_adjustment_enabled: dowAdjustmentEnabled,
          }),
        });
        setBulkResponses(Array.isArray(responseData) ? responseData : []);
        const successResponses = Array.isArray(responseData)
          ? responseData.filter((x) => !x.error)
          : [];
        const errorResponses = Array.isArray(responseData)
          ? responseData.filter((x) => x.error)
          : [];
        if (successResponses.length > 0) {
          setForecastedSKU(
            successResponses.map((x) => ({
              sku: x.data.sku,
              _id: x.data.forecast_data_id,
            })),
          );
        }
        // Category forecasts run in the background — show toast and return early
        if (responseData?.backgroundTask) {
          toast.info(
            `Category forecast is running in the background. You will receive a notification when it's done.`,
            { autoClose: 3000 },
          );
          return;
        }
        if (!responseData || responseData.length === 0) {
          toast.error(
            "Unable to generate forecast now. Please try again later!",
            { autoClose: 3000 },
          );
        } else if (successResponses.length === 0) {
          // All SKUs returned errors
          setBulkModalOpen(true);
        } else if (
          (forecastBy?.toLowerCase() === "product" ||
            forecastBy?.toLowerCase() === "similar product") &&
          successResponses.length > 1
        ) {
          // Load all SKU results together for product-level view
          // Build a map of product _id → product object so we can restore names
          const productNameMap = {};
          selectedInventories.forEach((x) => {
            if (x.product?._id) productNameMap[x.product._id] = x.product;
          });
          const allResults = await Promise.all(
            successResponses.map((item) =>
              forecastDataService.get(item.data.forecast_data_id),
            ),
          );
          const allForecastData = allResults.map((res) => {
            const inputProduct = res.data.input?.product;
            const resolvedProduct =
              typeof inputProduct === "string"
                ? productNameMap[inputProduct] || {
                    _id: inputProduct,
                    name: inputProduct,
                  }
                : inputProduct;
            return {
              ...res.data,
              forecast_data_id: res.data._id,
              input: {
                ...res.data.input,
                product: resolvedProduct,
              },
            };
          });
          setForecastData(allForecastData, null);
          forecastDataSet = true;
          // if (errorResponses.length > 0) setBulkModalOpen(true);
        } else if (
          successResponses.length === 1 &&
          errorResponses.length === 0
        ) {
          const res = await forecastDataService.get(
            successResponses[0].data.forecast_data_id,
          );
          const productNameMap = {};
          selectedInventories.forEach((x) => {
            if (x.product?._id) productNameMap[x.product._id] = x.product;
          });
          const isSimilarProduct = Object.keys(similarProductMap).length > 0;
          let resolvedProduct;
          let displaySku;
          if (isSimilarProduct) {
            // Always show the actual item's SKU and product, not the similar product used for forecasting
            resolvedProduct = selectedInventories[0].product;
            displaySku = selectedInventories[0].sku;
          } else {
            const inputProduct = res.data.input?.product;
            resolvedProduct =
              typeof inputProduct === "string"
                ? productNameMap[inputProduct] || {
                    _id: inputProduct,
                    name: inputProduct,
                  }
                : inputProduct;
            displaySku = res.data.sku;
          }
          setForecastData(
            {
              ...res.data,
              forecast_data_id: res.data._id,
              input: { ...res.data.input, product: resolvedProduct },
            },
            displaySku,
          );
          forecastDataSet = true;
        } else {
          // Multiple successes (non-product), or 1 success mixed with errors
          setBulkModalOpen(true);
        }
      } catch (err) {
        console.error("Forecast failed for", selectedInventories, err);
      } finally {
        _forecastCallbacks.current.onRefresh?.();
        _forecastCallbacks.current.onClearSelected?.();
        if (!forecastDataSet) {
          clearForecastingData();
        }
      }
    } catch (error) {
      console.error("Bulk forecast error", error);
      toast.error("Failed to request forecast");
    } finally {
      if (!forecastDataSet) {
        clearForecastingData();
      }
    }
  };

  const handleForecastSubmit = () => {
    if (forecastBy === "Similar Product") {
      setForecastDialogOpen(false);
      setSimilarProducts({});
      setSimilarProductSelections({});
      setSimilarActiveTab(0);
      setSimilarProductLoading(true);
      setSimilarProductDialogOpen(true);
      inventoryService
        .fetchSimilarProduct({
          products: forecastItems.map((x) => x.product._id),
        })
        .then((response) => {
          setSimilarProducts(
            response.reduce((acc, curr) => {
              acc[curr.requestProduct._id] = curr.result?.final?.products || [];
              return acc;
            }, {}),
          );
        })
        .catch((error) => {
          console.error("Failed to fetch similar products:", error);
          toast.error("Failed to fetch similar products");
          setSimilarProductDialogOpen(false);
        })
        .finally(() => setSimilarProductLoading(false));
    } else {
      handleBulkForecast();
      setForecastDialogOpen(false);
    }
  };

  const handleSimilarProductForecast = () => {
    setSimilarProductDialogOpen(false);
    handleBulkForecast(similarProductSelections);
  };

  const openForecast = (skuValue, expected) => {
    setSku(skuValue);
    setExpectedTime(expected);
    setOpen(true);
  };

  const closeForecast = () => {
    setOpen(false);
    setData(null);
    setLoading(false);
    setSku(null);
    setExpectedTime(null);
  };

  const setForecastLoading = (payload) => {
    console.log(payload);

    // payload can be {open, sku, expectedTime}
    if (typeof payload === "object") {
      if (payload.open !== undefined) {
        setLoading(payload.open);
        setOpen(payload.open);
      }
      if (payload.sku !== undefined) setSku(payload.sku);
      if (payload.expectedTime !== undefined)
        setExpectedTime(payload.expectedTime);
    } else {
      setLoading(payload);
    }
  };

  const setForecastData = async (d, sku) => {
    let res = await staticDataService.fetchByType("SHOW_FORECAST_EXPLANATION");
    if (res.data.length > 0) {
      setShowExplanation(res.data[0].value);
    }
    setLoading(false);
    setOpen(true);
    if (sku) {
      setSku(sku);
    }
    setData(d);
  };

  const onAcceptMetrics = async (forecastItem) => {
    try {
      await inventoryService.updateForecastMetrics({
        sku: forecastItem.sku,
        client: forecastItem.client?._id || forecastItem.client,
        forecast_data_id: forecastItem.forecast_data_id,
        warehouse: forecastItem.input?.warehouse,
        provider: forecastItem.input?.channel,
        forecast_type: forecastType,
        obj: {
          reorder_point: Math.round(forecastItem.metrics.reorder_point),
          next_reorder_date: forecastItem.metrics.reorder_by_date,
        },
      });
      toast.success("Inventory updated with forecast metrics successfully");
    } catch (error) {
      console.error(error);
      toast.error("Failed to accept forecast metrics");
    } finally {
      closeForecast();
    }
  };

  const closeAndDiscard = async (forecastItem) => {
    try {
      await inventoryService.discardForecastMetrics(
        forecastItem.forecast_data_id,
      );
    } catch (err) {
      console.error("Error discarding forecast metrics", err);
    } finally {
      closeForecast();
    }
  };

  const onAcceptAllMetrics = async (items) => {
    try {
      await Promise.all(
        items.map((item) =>
          inventoryService.updateForecastMetrics({
            sku: item.sku,
            client: item.client?._id || item.client,
            forecast_data_id: item.forecast_data_id,
            warehouse: item?.input?.warehouse,
            provider: item?.input?.channel,
            forecast_type: forecastType,
            obj: {
              reorder_point: Math.round(item.metrics?.reorder_point || 0),
              next_reorder_date: item.metrics?.reorder_by_date,
            },
          }),
        ),
      );
      toast.success("All SKUs updated with forecast metrics successfully");
    } catch (error) {
      console.error(error);
      toast.error("Failed to accept forecast metrics");
    } finally {
      closeForecast();
    }
  };

  const closeAndDiscardAll = async (items) => {
    try {
      await Promise.all(
        items.map((item) =>
          inventoryService.discardForecastMetrics(item.forecast_data_id),
        ),
      );
    } catch (err) {
      console.error("Error discarding all forecast metrics", err);
    } finally {
      closeForecast();
    }
  };

  // Silent variants — perform the API action without closing the modal.
  // The modal manages its own item-removal and close-when-empty logic.
  const acceptMetricsSilent = async (forecastItem) => {
    await inventoryService.updateForecastMetrics({
      sku: forecastItem.sku,
      client: forecastItem.client?._id || forecastItem.client,
      forecast_data_id: forecastItem.forecast_data_id,
      warehouse: forecastItem?.input?.warehouse,
      provider: forecastItem?.input?.channel,
      product:
        forecastItem?.input?.product?._id || forecastItem?.input?.product,
      forecast_type: forecastType,
      obj: {
        reorder_point: Math.round(forecastItem.metrics?.reorder_point || 0),
        next_reorder_date: forecastItem.metrics?.reorder_by_date,
      },
    });
    if (_forecastCallbacks.current.onAccept) {
      _forecastCallbacks.current.onAccept(forecastItem);
    }
  };

  const discardMetricsSilent = async (forecastItem) => {
    await inventoryService.discardForecastMetrics(
      forecastItem.forecast_data_id,
    );
  };

  const loadDataBySku = async (item) => {
    try {
      let res = await forecastDataService.get(item._id);
      setData({ ...res.data, forecast_data_id: res.data._id });
      setSku(res.data.sku);
    } catch (error) {
      console.error("Error fetching next forecast data:", error);
    }
  };

  const value = {
    open,
    data,
    loading,
    sku,
    expectedTime,
    openForecast,
    closeForecast,
    setForecastLoading,
    setForecastData,
    onAcceptMetrics,
    onAcceptAllMetrics,
    closeAndDiscard,
    closeAndDiscardAll,
    acceptMetricsSilent,
    discardMetricsSilent,
    setForecastedSKU,
    forecastedSKU,
    loadDataBySku,
    showExplanation,
    forecastProductName,
    forecastProductNames:
      forecastBy?.toLowerCase() === "product"
        ? [
            ...new Set(
              forecastItems.map((x) => x.product?.name || x.name || x.sku),
            ),
          ]
        : [],
    // Forecast params modal
    forecastDialogOpen,
    setForecastDialogOpen,
    forecastItems,
    openForecastParamsModal,
    horizon,
    setHorizon,
    horizonType,
    setHorizonType,
    selectedWarehouse,
    setSelectedWarehouse,
    selectedChannel,
    setSelectedChannel,
    bulkResponses,
    setBulkResponses,
    bulkModalOpen,
    setBulkModalOpen,
    forecastBy,
    setForecastBy,
    forecastByLocked,
    discountMin,
    setDiscountMin,
    discountMax,
    setDiscountMax,
    discountType,
    setDiscountType,
    discountMode,
    setDiscountMode,
    distributedDiscounts,
    setDistributedDiscounts,
    discountSectionCollapsed,
    setDiscountSectionCollapsed,
    percentageRange,
    handlePercentageRangeChange,
    similarProductDialogOpen,
    setSimilarProductDialogOpen,
    similarProducts,
    setSimilarProducts,
    similarProductSelections,
    setSimilarProductSelections,
    similarActiveTab,
    setSimilarActiveTab,
    similarProductLoading,
    handleForecastSubmit,
    handleSimilarProductForecast,
    clearForecastingData,
    forecastType,
    includeLostSales,
    setIncludeLostSales,
    searchWindowDays,
    setSearchWindowDays,
    demandWindowDays,
    setDemandWindowDays,
    dowAdjustmentEnabled,
    setDowAdjustmentEnabled,
    editingLostSales,
    setEditingLostSales,
    leadTimeDays,
    setLeadTimeDays,
    safetyStockPct,
    setSafetyStockPct,
  };

  return (
    <ForecastContext.Provider value={value}>
      {children}
    </ForecastContext.Provider>
  );
};

export const useForecast = () => useContext(ForecastContext);
